/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { DataFlowDataType } from 'app/model/dataflow/dataflow-datatype';
import { escapeRegExp } from 'lodash';
import { DataFlowTypesUtils } from './dataflow-type-utilities';

export class DataFlowExpressionFunctionImpls {
    public static dataflow_startsWith(...objects: any[]): boolean {
        if (objects && objects.length === 2) {
            const str1 = objects[0].returnValue as string;
            const str2 = objects[1].returnValue as string;

            if (!!str1 && !!str2) {
                return str1.startsWith(str2);
            }
        }
        return undefined;
    }

    public static dataflow_endsWith(...objects: any[]): boolean {
        if (objects && objects.length === 2) {
            const str1 = objects[0].returnValue as string;
            const str2 = objects[1].returnValue as string;

            if (!!str1 && !!str2) {
                return str1.endsWith(str2);
            }
        }
        return undefined;
    }

    public static dataflow_equalsIgnoreCase(...objects: any[]): boolean {
        if (objects && objects.length === 2) {
            const referenceString: string = objects[0].returnValue as string;
            const compareString: string = objects[1].returnValue as string;
            return referenceString.localeCompare(compareString, undefined, { sensitivity: 'accent' }) === 0;
        }
        return undefined;
    }

    public static dataflow_toString(object: any): string {
        if (object && typeof(object.returnValue) === 'string') {
            return object.returnValue;
        } else if (object && typeof(object.returnValue) === 'number') {
            return `${object.returnValue}`;
        }
        return '';
    }

    public static dataflow_concat(...objects: any[]): string {
        if (objects) {
            const rights: string[] = objects.map((object) => object.returnValue as string);
            return ''.concat(...rights);
        }

        return undefined;
    }

    public static dataflow_substring(...objects: any[]): string {
        if (objects && objects.length >= 2) {
            const str: string = objects[0].returnValue as string;
            const start: number = objects[1].returnValue as number;
            let length: number = str ? str.length : 0;
            if (objects[2]) {
                length = objects[2].returnValue as number;
            }

            if (str && start && length) {
                return str.substring(start - 1, start - 1 + length);
            }
        }

        return undefined;
    }

    public static dataflow_upper(...objects: any[]): string {
        if (objects && objects.length === 1) {
            const str: string = objects[0].returnValue as string;

            if (str) {
                return str.toUpperCase();
            }
        }

        return undefined;
    }

    public static dataflow_lower(...objects: any[]): string {
        if (objects && objects.length === 1) {
            const str: string = objects[0].returnValue as string;

            if (str) {
                return str.toLowerCase();
            }
        }

        return undefined;
    }

    public static dataflow_initCap(...objects: any[]): string {
        if (objects && objects.length === 1) {
            const str: string = objects[0].returnValue as string;
            return str.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase());
        }
        return undefined;
    }

    public static dataflow_trim(...objects: any[]): string {
        if (objects) {
            const str: string = objects[0].returnValue as string;
            let trimStr: string = null;

            if (objects.length === 2) {
                trimStr = objects[1].returnValue as string;
            }

            if (str) {
                trimStr = (trimStr && trimStr.length > 0) ? trimStr : '\\s';

                const pattern = new RegExp('^[' + trimStr + ']+|[' + trimStr + ']+$', 'g');
                return str.replace(pattern, '');
            }
        }

        return undefined;
    }

    public static dataflow_ltrim(...objects: any[]): string {
        if (objects) {
            const str: string = objects[0].returnValue as string;
            let trimStr: string = null;

            if (objects.length === 2) {
                trimStr = objects[1].returnValue as string;
            }

            if (str) {
                trimStr = (trimStr && trimStr.length > 0) ? trimStr : '\\s';

                const pattern = new RegExp('^[' + trimStr + ']+', 'g');
                return str.replace(pattern, '');
            }
        }

        return undefined;
    }

    public static dataflow_rtrim(...objects: any[]): string {
        if (objects) {
            const str: string = objects[0].returnValue as string;
            let trimStr: string = null;

            if (objects.length === 2) {
                trimStr = objects[1].returnValue as string;
            }

            if (str) {
                trimStr = (trimStr && trimStr.length > 0) ? trimStr : '\\s';

                const pattern = new RegExp('[' + trimStr + ']+$', 'g');
                return str.replace(pattern, '');
            }
        }

        return undefined;
    }

    public static dataflow_translate(...objects: any[]): string {
        if (objects && objects.length === 3) {
            const str: string = objects[0].returnValue as string;
            const replacedfrom: string = objects[1].returnValue as string;
            const replacedTo: string = objects[2].returnValue as string;

            if (str && replacedfrom && replacedTo) {
                return str.replace(replacedfrom, replacedTo);
            }
        }

        return undefined;
    }

    public static dataflow_reverse(...objects: any[]): string {
        if (objects) {
            const str: string = objects[0].returnValue as string;

            const splitString = str.split('');
            const reverseArray = splitString.reverse();
            return reverseArray.join('');
        }

        return undefined;
    }

    public static dataflow_left(...objects: any[]): string {
        if (objects && objects.length === 2) {
            const str: string = objects[0].returnValue as string;
            const numberOfCharacters: number = objects[1].returnValue as number;

            if (str && numberOfCharacters) {
                return str.substring(0, numberOfCharacters);
            }
        }

        return undefined;
    }

    public static dataflow_right(...objects: any[]): string {
        if (objects && objects.length === 2) {
            const str: string = objects[0].returnValue as string;
            const numberOfCharacters: number = objects[1].returnValue as number;

            if (str && numberOfCharacters) {
                return str.substring(str.length - numberOfCharacters);
            }
        }

        return undefined;
    }

    public static dataflow_length(...objects: any[]): number {
        if (objects) {
            const str: string = objects[0].returnValue as string;

            if (str) {
                return str.length;
            }
        }

        return undefined;
    }

    public static dataflow_lpad(...objects: any[]): string {
        if (objects && objects.length === 3) {
            const str: string = objects[0].returnValue as string;
            const finalPaddedLength: number = objects[1].returnValue as number;
            const paddingCharacters: string = objects[2].returnValue as string;

            if (str && finalPaddedLength && paddingCharacters && str.length < finalPaddedLength) {
                const paddingArray = new Array(finalPaddedLength - str.length);
                for (let i = 0; i < finalPaddedLength - str.length; i++) {
                    paddingArray[i] = paddingCharacters.charAt(i % paddingCharacters.length);
                }

                const splitString = str.split('');
                return [...paddingArray, ...splitString].join('');
            }
            return str;
        }

        return undefined;
    }

    public static dataflow_rpad(...objects: any[]): string {
        if (objects && objects.length === 3) {
            const str: string = objects[0].returnValue as string;
            const finalPaddedLength: number = objects[1].returnValue as number;
            const paddingCharacters: string = objects[2].returnValue as string;

            if (str && finalPaddedLength && paddingCharacters && str.length < finalPaddedLength) {
                const paddingArray = new Array(finalPaddedLength - str.length);
                for (let i = 0; i < finalPaddedLength - str.length; i++) {
                    paddingArray[i] = paddingCharacters.charAt(i % paddingCharacters.length);
                }

                const splitString = str.split('');
                return [...splitString, ...paddingArray].join('');
            }
            return str;
        }

        return undefined;
    }

    public static dataflow_locate(...objects: any[]): number {
        if (objects && objects.length >= 2) {
            const substr: string = objects[0].returnValue as string;
            const str: string = objects[1].returnValue as string;

            let start: number = 1;

            if (objects.length === 3) {
                start = objects[2].returnValue as number;
            }

            if (str && substr && start) {
                return str.substring(start - 1).indexOf(substr) + 1;
            }
        }

        return undefined;
    }

    public static dataflow_instr(...objects: any[]): number {
        if (objects && objects.length === 2) {
            const str = objects[0];
            const substr = objects[1];
            return DataFlowExpressionFunctionImpls.dataflow_locate(substr, str);
        }

        return undefined;
    }

    public static dataflow_replace(...objects: any[]): string {
        if (objects && objects.length >= 2) {
            const str: string = objects[0].returnValue as string;
            const replacedFrom: string = escapeRegExp(objects[1].returnValue as string);
            const replacedTo: string = objects[2] ? objects[2].returnValue as string : '';

            // Use != null to allow the empty string as a replacement value
            if (str && replacedFrom && replacedTo != null) {
                const pattern = new RegExp(replacedFrom, 'g');
                return str.replace(pattern, replacedTo);
            }
        }

        return undefined;
    }

    public static dataflow_regexReplace(...objects: any[]): string {
        if (objects && objects.length === 3) {
            const str: string = objects[0].returnValue as string;
            const replacedFrom: string = objects[1].returnValue as string;
            const replacedTo: string = objects[2].returnValue as string;

            // Use != null to allow the empty string as a replacement value
            if (str && replacedFrom && replacedTo != null) {
                const pattern = new RegExp(replacedFrom, 'g');
                return str.replace(pattern, replacedTo);
            }
        }

        return undefined;
    }

    public static dataflow_regexMatch(...objects: any[]): boolean {
        if (objects && objects.length === 2) {
            const str: string = objects[0].returnValue as string;
            const pattern: string = objects[1].returnValue as string;

            if (str && pattern) {
                const patternReg = new RegExp(pattern);
                return patternReg.test(str);
            }
        }

        return undefined;
    }

    public static dataflow_regexExtract(...objects: any[]): string {
        if (objects && objects.length >= 2) {
            const stringToMatchOn: string = objects[0].returnValue as string;
            const regexToFind: string = objects[1].returnValue as string;
            let matchGroupIndex: number = 1;
            if (objects.length === 3) {
                matchGroupIndex = objects[2].returnValue as number;
            }

            if (stringToMatchOn && regexToFind && matchGroupIndex) {
                const regex = new RegExp(`${regexToFind}`);
                const matches = stringToMatchOn.match(regex);
                if (matches) {
                    return matches[matchGroupIndex];
                }

                return stringToMatchOn;
            }
        }
        return undefined;
    }

    public static dataflow_split(...objects: any[]): string[] {
        if (objects && objects.length === 2) {
            const stringToSplit = objects[0].returnValue as string;
            const delimiter = objects[1].returnValue as string;

            if (!!stringToSplit && !!delimiter) {
                return stringToSplit.split(delimiter);
            }
        }
        return undefined;
    }

    public static dataflow_typeMatch(...objects: any[]): boolean {
        if (objects && objects.length === 2) {
            const actualType: string = objects[0].returnValue as string;
            const actualDataFlowType: DataFlowDataType = actualType && actualType.startsWith('decimal') ? 'decimal' : actualType as DataFlowDataType;
            const expectType: DataFlowDataType = objects[1].returnValue as DataFlowDataType;

            return DataFlowTypesUtils.typeMatch(actualDataFlowType, expectType);
        }
        return false;
    }

    public static dataflow_true(...objects: any[]): boolean {
        return true;
    }

    public static dataflow_false(...objects: any[]): boolean {
        return false;
    }

    public static dataflow_null(...objects: any[]): null {
        return null;
    }

    public static dataflow_isNull(...objects: any[]): boolean {
        if (objects && objects.length === 1) {
            return objects[0].returnValue == null;
        }
        return undefined;
    }

    public static dataflow_coalesce(...objects: any[]): any {
        if (objects && objects.length >= 1) {
            for (const object of objects) {
                if (object.returnValue != null) {
                    return object.returnValue;
                }
            }
        }
        return undefined;
    }

    public static dataflow_iif(...objects: any): any {
        if (objects && objects.length >= 2) {
            const conditionResult: boolean = objects[0].returnValue as boolean;
            const trueValue: any = objects[1].returnValue;
            const falseValue: any = objects[2] ? objects[2].returnValue : null;

            return conditionResult === true ? trueValue : falseValue;
        }
        return undefined;
    }

    public static dataflow_in(...objects: any): any {
        if (objects && objects.length === 2 && objects[0] && objects[0].value && objects[0].value.children) {
            const arrayToTest = objects[0].value.children.map((element: any) => element.returnValue);
            const valueToFind: any = objects[1].returnValue;

            return arrayToTest.includes(valueToFind);
        }
        return undefined;
    }
}
